import { generateDeploymentKey } from "../react-query/keyFactory";
import { useApiQuery } from "./useApiQuery";
import { Deployment } from "../types/Deployment";
import { toTemporalZonedDateTime, toDate } from "../shared/lib/temporal";
import { useRef, useMemo } from "react";
import isEqual from "lodash/isEqual";

export const useFetchDeploymentsBySiteId = (
    siteId: string,
    timezone = "UTC",
) => {
    const queryKey = generateDeploymentKey(siteId);
    const { data: rawDeployments = [], ...queryResult } = useApiQuery<
        Deployment[]
    >({
        queryKey,
        endpoint: `sites/${siteId}/deployments`,
        enabled: !!siteId,
        select: (data) => {
            return data.map((deployment) => {
                // Convert ISO strings to Date objects in the specified timezone
                const startTemporal = toTemporalZonedDateTime(
                    typeof deployment.startTime === "string"
                        ? new Date(deployment.startTime)
                        : (deployment.startTime as Date),
                    timezone,
                );
                const endTemporal = toTemporalZonedDateTime(
                    typeof deployment.endTime === "string"
                        ? new Date(deployment.endTime)
                        : (deployment.endTime as Date),
                    timezone,
                );

                return {
                    ...deployment,
                    startTime: toDate(startTemporal),
                    endTime: toDate(endTemporal),
                };
            });
        },
    });

    // Memoize deployments array for stable reference
    const prevDeploymentsRef = useRef<Deployment[]>([]);
    const deployments = useMemo(() => {
        if (isEqual(prevDeploymentsRef.current, rawDeployments)) {
            return prevDeploymentsRef.current;
        }
        prevDeploymentsRef.current = rawDeployments;
        return rawDeployments;
    }, [rawDeployments]);

    return { deployments, ...queryResult };
};
